import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const DownloadContext = createContext(null);

export function useDownloads() {
  return useContext(DownloadContext);
}

export function DownloadProvider({ children }) {
  const [downloads, setDownloads] = useState([]);
  const { isAuthenticated } = useAuth() || {};

  // ref с актуальным списком — нужен интервалу опроса
  const downloadsRef = useRef(downloads);
  downloadsRef.current = downloads;

  const removeJob = useCallback((jobId) => {
    setDownloads((prev) => prev.filter((d) => d.jobId !== jobId));
  }, []);

  // спросить у сервера список своих задач — это источник правды для плашки
  const fetchMyDownloads = useCallback(async () => {
    let list;
    try {
      const res = await axios.get('/api/reports/my-downloads');
      list = res.data.downloads || [];
    } catch {
      return;
    }
    setDownloads(
      list.map((j) => ({
        jobId: j.id,
        name: j.name,
        total: j.total,
        done: j.done,
        percent: j.percent,
        status: j.status,
        error: j.error,
      }))
    );
  }, []);

  // запустить отслеживание новой задачи (jobId уже получен с бэка)
  const startDownload = useCallback((job) => {
    setDownloads((prev) => {
      if (prev.some((d) => d.jobId === job.jobId)) return prev;
      // оптимистично показываем плашку сразу, не дожидаясь первого опроса
      return [...prev, { ...job, done: 0, percent: 0, status: 'running' }];
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // скачивание по кнопке — нативное (браузер стримит файл на диск).
  // Это надёжнее XHR-блоба для больших архивов: не держим 80+ МБ в памяти JS
  // и не упираемся в ошибки HTTP/2. Авторизация проходит по cookie `token`.
  const manualDownload = useCallback((jobId) => {
    const base = axios.defaults.baseURL || '';
    const link = document.createElement('a');
    link.href = `${base}/api/reports/download-file/${jobId}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  // крестик: удаляем архив на сервере и скрываем плашку
  const dismissDownload = useCallback(async (jobId) => {
    try {
      await axios.post(`/api/reports/download-delete/${jobId}`);
    } catch {
      // даже если не удалось — убираем из плашки локально
    }
    removeJob(jobId);
  }, [removeJob]);

  // как только авторизация подтвердилась — подтягиваем задачи. Это чинит случай,
  // когда на старте запрос уходил раньше готовности токена и ловил 401.
  useEffect(() => {
    if (isAuthenticated) fetchMyDownloads();
  }, [isAuthenticated, fetchMyDownloads]);

  // возврат на вкладку — тоже обновляем
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchMyDownloads();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchMyDownloads]);

  // опрос прогресса — только пока есть задача в работе (иначе сервер не дёргаем)
  useEffect(() => {
    const interval = setInterval(() => {
      if (downloadsRef.current.some((d) => d.status === 'running')) {
        fetchMyDownloads();
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [fetchMyDownloads]);

  const refreshDownloads = useCallback(() => fetchMyDownloads(), [fetchMyDownloads]);

  return (
    <DownloadContext.Provider value={{ downloads, startDownload, manualDownload, dismissDownload, removeJob, refreshDownloads }}>
      {children}
    </DownloadContext.Provider>
  );
}

export function DownloadBanner() {
  const ctx = useDownloads();
  if (!ctx || ctx.downloads.length === 0) return null;
  const { downloads, manualDownload, dismissDownload } = ctx;

  return (
    <div style={styles.container}>
      {downloads.map((job) => (
        <div key={job.jobId} style={styles.card}>
          <div style={styles.row}>
            <span style={styles.name} title={job.name}>{job.name}</span>
            <div style={styles.barTrack}>
              <div
                style={{
                  ...styles.barFill,
                  width: `${job.percent || 0}%`,
                  background: job.status === 'error' ? 'var(--accent-danger)' : 'var(--accent-primary)',
                }}
              />
            </div>
            <span style={styles.percent}>
              {job.status === 'error' ? 'Ошибка' : `${job.percent || 0}%`}
            </span>
            {job.status === 'running' && (
              <span style={styles.statusText}>Сборка, подождите…</span>
            )}
            {job.status === 'ready' && (
              <button style={styles.btn} onClick={() => manualDownload(job.jobId, job.name)}>
                Скачать
              </button>
            )}
            {(job.status === 'ready' || job.status === 'error') && (
              <button
                style={styles.closeBtn}
                title="Удалить архив"
                onClick={() => dismissDownload(job.jobId)}
              >
                ✕
              </button>
            )}
          </div>
          <div style={styles.note}>
            {job.status === 'error' && job.error}
            {job.status === 'running' &&
              `Готово ${job.done || 0} из ${job.total || 0} файлов. Можно закрыть сайт — сборка продолжится на сервере, вернётесь позже и скачаете архив.`}
            {job.status === 'ready' &&
              'Архив готов. Нажмите «Скачать», затем ✕ чтобы убрать и удалить с сервера.'}
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: '1140px',
    margin: '12px auto 0',
    padding: '0 12px',
  },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderLeft: '4px solid var(--accent-primary)',
    borderRadius: 'var(--border-radius-sm, 8px)',
    boxShadow: 'var(--shadow-md)',
    padding: '10px 14px',
    color: 'var(--text-primary)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  name: {
    fontWeight: 600,
    fontSize: '14px',
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
  barTrack: {
    flex: 1,
    minWidth: '120px',
    height: '10px',
    background: 'var(--bg-tertiary)',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.3s ease',
  },
  percent: {
    fontSize: '13px',
    fontWeight: 600,
    minWidth: '42px',
    textAlign: 'right',
    color: 'var(--text-primary)',
  },
  statusText: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  btn: {
    background: 'var(--accent-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    color: 'var(--text-muted)',
  },
  note: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    marginTop: '6px',
  },
};
